import { useState, useCallback, useRef } from 'react';
import { MicroLearningAPI } from '../apis/micro-learning.api';
import Cookies from 'js-cookie';

interface InjectionState {
  injectedModule: any | null;
  currentAttemptId: string | null;
  isChecking: boolean;
  guaranteedModule: any | null;
  guaranteedAttemptId: string | null;
  hasCheckedGuaranteed: boolean;
  isCheckingGuaranteed: boolean; // Flag to prevent race condition
  // Track modules shown this session to prevent duplicates
  shownModuleIds: Set<string>;
}

interface UseSimpleInjectionReturn {
  injectedModule: any | null;
  guaranteedModule: any | null;
  isChecking: boolean;
  checkAndInject: (articlesLoaded: number, timeOnPlatform: number) => Promise<void>;
  checkGuaranteedPosition: () => Promise<void>;
  recordModuleStart: () => Promise<void>;
  recordModuleComplete: (score: number, quizPassed: boolean, timeSpent: number) => Promise<void>;
  recordModuleAbandon: () => Promise<void>;
  recordGuaranteedModuleStart: () => Promise<void>;
  recordGuaranteedModuleComplete: (score: number, quizPassed: boolean, timeSpent: number) => Promise<void>;
  recordGuaranteedModuleAbandon: () => Promise<void>;
  clearModule: () => void;
  clearGuaranteedModule: () => void;
  resetForNewFeed: () => void;
}

/**
 * Simplified intelligent injection - just check when called, no intervals
 * Call this when articles are loaded
 * 
 * Supports:
 * - Regular injection: Based on scroll/time thresholds
 * - Guaranteed position 4: Always try to show a module at position 4 for first impressions
 */
export const useSimpleInjection = (): UseSimpleInjectionReturn => {
  const [state, setState] = useState<InjectionState>({
    injectedModule: null,
    currentAttemptId: null,
    isChecking: false,
    guaranteedModule: null,
    guaranteedAttemptId: null,
    hasCheckedGuaranteed: false,
    isCheckingGuaranteed: false,
    shownModuleIds: new Set(),
  });

  const lastCheckTimeRef = useRef<number>(0);
  const MIN_CHECK_INTERVAL_MS = 500; // Still throttle to max once per .5 seconds
  const token = Cookies.get('token');
  
  // Track shown module IDs in a ref for immediate access
  const shownModuleIdsRef = useRef<Set<string>>(new Set());

  /**
   * Check for guaranteed position 4 module on initial load
   * Only runs once per session, requires auth
   */
  const checkGuaranteedPosition = useCallback(async () => {
    // Only check once per session
    if (state.hasCheckedGuaranteed || state.isCheckingGuaranteed) {
      return;
    }

    // Must be logged in
    if (!token) {
      console.log('[Injection] ⏭️ Guaranteed: User not logged in');
      setState(prev => ({ ...prev, hasCheckedGuaranteed: true }));
      return;
    }

    // Already have a guaranteed module
    if (state.guaranteedModule) {
      return;
    }

    // Set flag to prevent race condition with regular injection
    setState(prev => ({ ...prev, isCheckingGuaranteed: true }));

    try {
      console.log('[Injection] 🎯 Checking guaranteed position 4...');
      
      const decision = await MicroLearningAPI.getGuaranteedPositionModule(4);
      
      console.log('[Injection] Guaranteed decision:', decision);

      if (decision.inject && decision.module) {
        // Track this module as shown
        shownModuleIdsRef.current.add(decision.module.id);
        
        const { attemptId } = await MicroLearningAPI.recordInjection({
          moduleId: decision.module.id,
          articlesScrolled: 0,
          timeOnPlatform: 0,
          injectionReason: 'guaranteed_position_4',
        });

        console.log('[Injection] ✅ Guaranteed module set:', decision.module.title, '(ID:', decision.module.id, ')');
        
        setState(prev => ({
          ...prev,
          guaranteedModule: decision.module,
          guaranteedAttemptId: attemptId,
          hasCheckedGuaranteed: true,
          isCheckingGuaranteed: false,
          shownModuleIds: new Set([...prev.shownModuleIds, decision.module.id]),
        }));
      } else {
        console.log('[Injection] ❌ No guaranteed module:', decision.reason);
        setState(prev => ({ ...prev, hasCheckedGuaranteed: true, isCheckingGuaranteed: false }));
      }
    } catch (error) {
      console.error('[Injection] ⚠️ Guaranteed check failed:', error);
      setState(prev => ({ ...prev, hasCheckedGuaranteed: true, isCheckingGuaranteed: false }));
    }
  }, [state.hasCheckedGuaranteed, state.isCheckingGuaranteed, state.guaranteedModule, token]);

  /**
   * Check if we should inject and do it
   * Called on "load more" events as user scrolls
   */
  const checkAndInject = useCallback(async (articlesLoaded: number, timeOnPlatform: number) => {
    // Skip if already have a regular module showing (not cleared yet)
    if (state.injectedModule) {
      console.log('[Injection] ⏭️ Skipped: regular module already showing');
      return;
    }

    // Skip if guaranteed check is in progress - wait for it to complete first
    if (state.isCheckingGuaranteed) {
      console.log('[Injection] ⏭️ Skipped: guaranteed check in progress, waiting...');
      return;
    }

    // Skip if already checking
    if (state.isChecking) {
      console.log('[Injection] ⏭️ Skipped: check in progress');
      return;
    }

    // Throttle: Don't check more than once per interval
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTimeRef.current;
    if (timeSinceLastCheck < MIN_CHECK_INTERVAL_MS) {
      console.log('[Injection] ⏱️ Throttled:', Math.floor(timeSinceLastCheck / 1000), 's since last check');
      return;
    }

    setState(prev => ({ ...prev, isChecking: true }));

    try {
      // Pass already-shown module IDs to backend so it doesn't return them
      const excludeModuleIds = Array.from(shownModuleIdsRef.current);
      
      console.log('[Injection] 🔍 Checking...', { articlesLoaded, timeOnPlatform, excludeModuleIds });

      const decision = await MicroLearningAPI.shouldInjectModule({
        articlesScrolled: articlesLoaded,
        timeOnPlatform,
        excludeModuleIds,
      });

      console.log('[Injection] Decision:', decision);

      if (decision.inject && decision.module) {
        // Double-check: skip if this module was already shown this session
        if (shownModuleIdsRef.current.has(decision.module.id)) {
          console.log('[Injection] ⏭️ Skipped: module already shown this session:', decision.module.id);
          setState(prev => ({ ...prev, isChecking: false }));
          lastCheckTimeRef.current = now;
          return;
        }

        // Track this module as shown
        shownModuleIdsRef.current.add(decision.module.id);

        const { attemptId } = await MicroLearningAPI.recordInjection({
          moduleId: decision.module.id,
          articlesScrolled: articlesLoaded,
          timeOnPlatform,
          injectionReason: decision.reason,
        });

        console.log('[Injection] ✅ Injected module:', decision.module.title, '(ID:', decision.module.id, ')');
        
        setState(prev => ({
          ...prev,
          injectedModule: decision.module,
          currentAttemptId: attemptId,
          isChecking: false,
          shownModuleIds: new Set([...prev.shownModuleIds, decision.module.id]),
        }));
        
        lastCheckTimeRef.current = now;
      } else {
        console.log('[Injection] ❌ Not injecting:', decision.reason);
        setState(prev => ({ ...prev, isChecking: false }));
        lastCheckTimeRef.current = now;
      }
    } catch (error) {
      console.error('[Injection] ⚠️ Check failed:', error);
      setState(prev => ({ ...prev, isChecking: false }));
    }
  }, [state.injectedModule, state.isChecking, state.isCheckingGuaranteed]);

  const recordModuleStart = useCallback(async () => {
    if (!state.currentAttemptId) return;

    try {
      await MicroLearningAPI.updateInjectionAttempt(state.currentAttemptId, {
        wasStarted: true,
      });
      console.log('[Injection] ▶️ Module started');
    } catch (error) {
      console.error('[Injection] Failed to record start:', error);
    }
  }, [state.currentAttemptId]);

  const recordModuleComplete = useCallback(
    async (score: number, quizPassed: boolean, timeSpent: number) => {
      if (!state.currentAttemptId) return;

      try {
        await MicroLearningAPI.updateInjectionAttempt(state.currentAttemptId, {
          wasCompleted: true,
          quizPassed,
          score,
          timeSpent,
        });
        console.log('[Injection] ✅ Module completed:', { score, quizPassed });
        
        // Clear attempt ID but keep module visible for completed/failed card
        setState(prev => ({ ...prev, currentAttemptId: null }));
      } catch (error) {
        console.error('[Injection] Failed to record completion:', error);
      }
    },
    [state.currentAttemptId]
  );

  const recordModuleAbandon = useCallback(async () => {
    if (!state.currentAttemptId) return;

    try {
      await MicroLearningAPI.updateInjectionAttempt(state.currentAttemptId, {
        abandoned: true,
      });
      console.log('[Injection] 🚪 Module abandoned');
      setState(prev => ({ ...prev, currentAttemptId: null }));
    } catch (error) {
      console.error('[Injection] Failed to record abandon:', error);
    }
  }, [state.currentAttemptId]);

  const clearModule = useCallback(() => {
    console.log('[Injection] 🗑️ Clearing module');
    setState(prev => ({
      ...prev,
      injectedModule: null,
      currentAttemptId: null,
      isChecking: false,
    }));
  }, []);

  // Guaranteed module handlers
  const recordGuaranteedModuleStart = useCallback(async () => {
    if (!state.guaranteedAttemptId) return;

    try {
      await MicroLearningAPI.updateInjectionAttempt(state.guaranteedAttemptId, {
        wasStarted: true,
      });
      console.log('[Injection] ▶️ Guaranteed module started');
    } catch (error) {
      console.error('[Injection] Failed to record guaranteed start:', error);
    }
  }, [state.guaranteedAttemptId]);

  const recordGuaranteedModuleComplete = useCallback(
    async (score: number, quizPassed: boolean, timeSpent: number) => {
      if (!state.guaranteedAttemptId) return;

      try {
        await MicroLearningAPI.updateInjectionAttempt(state.guaranteedAttemptId, {
          wasCompleted: true,
          quizPassed,
          score,
          timeSpent,
        });
        console.log('[Injection] ✅ Guaranteed module completed:', { score, quizPassed });
        
        setState(prev => ({ ...prev, guaranteedAttemptId: null }));
      } catch (error) {
        console.error('[Injection] Failed to record guaranteed completion:', error);
      }
    },
    [state.guaranteedAttemptId]
  );

  const recordGuaranteedModuleAbandon = useCallback(async () => {
    if (!state.guaranteedAttemptId) return;

    try {
      await MicroLearningAPI.updateInjectionAttempt(state.guaranteedAttemptId, {
        abandoned: true,
      });
      console.log('[Injection] 🚪 Guaranteed module abandoned');
      setState(prev => ({ ...prev, guaranteedAttemptId: null }));
    } catch (error) {
      console.error('[Injection] Failed to record guaranteed abandon:', error);
    }
  }, [state.guaranteedAttemptId]);

  const clearGuaranteedModule = useCallback(() => {
    console.log('[Injection] 🗑️ Clearing guaranteed module');
    setState(prev => ({
      ...prev,
      guaranteedModule: null,
      guaranteedAttemptId: null,
    }));
  }, []);

  /**
   * Reset for a new feed/tab - clears everything except shown module tracking
   * Shown modules are tracked for the whole session to prevent repetition
   */
  const resetForNewFeed = useCallback(() => {
    console.log('[Injection] 🔄 Resetting for new feed');
    setState(prev => ({
      ...prev,
      injectedModule: null,
      currentAttemptId: null,
      isChecking: false,
      guaranteedModule: null,
      guaranteedAttemptId: null,
      hasCheckedGuaranteed: false,
      isCheckingGuaranteed: false,
      // Keep shownModuleIds to prevent showing same module across tabs
    }));
  }, []);

  return {
    injectedModule: state.injectedModule,
    guaranteedModule: state.guaranteedModule,
    isChecking: state.isChecking,
    checkAndInject,
    checkGuaranteedPosition,
    recordModuleStart,
    recordModuleComplete,
    recordModuleAbandon,
    recordGuaranteedModuleStart,
    recordGuaranteedModuleComplete,
    recordGuaranteedModuleAbandon,
    clearModule,
    clearGuaranteedModule,
    resetForNewFeed,
  };
};

export default useSimpleInjection;

